/**
 * extractInsertOutline — token-aware INSERT statement walker. Returns the
 * target table, optional column list, body kind (`values` / `select` /
 * `default-values` / `unknown`), per-row value spans (when `values`), source
 * SELECT span (when `select`), optional RETURNING span, and the appropriate
 * conflict-clause span (PG `ON CONFLICT …` / MySQL `ON DUPLICATE KEY UPDATE`).
 *
 * Pure / structural — uses `tokenSkip` for string + comment + quoted-identifier
 * regions so dialect-specific quoting (backticks, brackets, dollar-tags, hash
 * comments) is invisible to brace counting (VQE-RO9 — no regex parsing).
 *
 * Low-level token helpers live in `insertParseHelpers.ts` so the file stays
 * focused on the statement-shape walker.
 */

import type { ParseOptions, SqlRange } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import {
  IDENT_CONT,
  IDENT_START,
  consumeParenGroup,
  isWordBoundaryBefore,
  readColumnList,
  readWord,
  skipWsAndComments,
  splitTopLevelCommasToText,
} from './insertParseHelpers';
import { readQualifiedTarget } from '../common/statementScan';

export type InsertBodyKind = 'values' | 'select' | 'default-values' | 'unknown';

export interface ValuesRow {
  /** Inclusive start of the row's `(` — useful for hover affordances. */
  readonly parenStart: number;
  /** Exclusive end of the row's `)` (one past the closing paren). */
  readonly parenEnd: number;
  /** Inclusive start of the body inside the parens (post `(`). */
  readonly bodyStart: number;
  /** Exclusive end of the body inside the parens (pre `)`). */
  readonly bodyEnd: number;
  /** Per-cell text values (top-level comma split, trimmed). */
  readonly cells: readonly string[];
}

export interface InsertOutline {
  /** True only if a recognizable INSERT keyword was found at the leading position. */
  readonly hasInsert: boolean;
  /** Target table text exactly as written (`schema.table` / quoted forms preserved). */
  readonly targetText: string | null;
  readonly targetStart: number | null;
  readonly targetEnd: number | null;
  /** Column list — null when the INSERT omits the column-list paren group. */
  readonly columns: readonly string[] | null;
  readonly columnsStart: number | null;
  readonly columnsEnd: number | null;
  readonly bodyKind: InsertBodyKind;
  /** VALUES rows — empty array unless `bodyKind === 'values'`. */
  readonly valuesRows: readonly ValuesRow[];
  /** Source SELECT body span — null unless `bodyKind === 'select'`. */
  readonly selectSourceStart: number | null;
  readonly selectSourceEnd: number | null;
  /** RETURNING expression list span (excluding the keyword) — null when absent. */
  readonly returningStart: number | null;
  readonly returningEnd: number | null;
  /** PG `ON CONFLICT … DO UPDATE SET …` / `ON CONFLICT … DO NOTHING` span. */
  readonly onConflictStart: number | null;
  readonly onConflictEnd: number | null;
  /** MySQL `ON DUPLICATE KEY UPDATE …` span (assignment list, post the keyword run). */
  readonly onDuplicateStart: number | null;
  readonly onDuplicateEnd: number | null;
}

const EMPTY_OUTLINE: InsertOutline = {
  hasInsert: false,
  targetText: null, targetStart: null, targetEnd: null,
  columns: null, columnsStart: null, columnsEnd: null,
  bodyKind: 'unknown', valuesRows: [],
  selectSourceStart: null, selectSourceEnd: null,
  returningStart: null, returningEnd: null,
  onConflictStart: null, onConflictEnd: null,
  onDuplicateStart: null, onDuplicateEnd: null,
};

export function extractInsertOutline(
  source: string,
  statementRange: SqlRange,
  options: ParseOptions,
): InsertOutline {
  const start = Math.max(0, statementRange.startOffset);
  const end = Math.max(start, Math.min(source.length, statementRange.endOffset));
  const ctx = skipContextFor(options);

  let i = skipWsAndComments(source, start, end);
  const insertWord = readWord(source, i, end);
  if (!insertWord || insertWord.upper !== 'INSERT') return EMPTY_OUTLINE;
  i = skipWsAndComments(source, insertWord.end, end);

  const intoWord = readWord(source, i, end);
  if (intoWord && intoWord.upper === 'INTO') {
    i = skipWsAndComments(source, intoWord.end, end);
  }

  const target = readQualifiedTarget(source, i, end);
  if (!target) return { ...EMPTY_OUTLINE, hasInsert: true };
  i = skipWsAndComments(source, target.end, end);

  let columns: readonly string[] | null = null;
  let columnsStart: number | null = null;
  let columnsEnd: number | null = null;
  if (i < end && source[i] === '(' && looksLikeColumnList(source, i, end)) {
    columnsStart = i;
    const groupEnd = consumeParenGroup(source, i, end, ctx);
    columnsEnd = groupEnd;
    columns = readColumnList(source, i + 1, groupEnd - 1, ctx);
    i = skipWsAndComments(source, groupEnd, end);
  }

  const body = parseInsertBody(source, i, end, ctx);
  const tail = parseInsertTail(source, body.bodyEndCursor, end, ctx);

  return {
    hasInsert: true,
    targetText: target.text,
    targetStart: target.start,
    targetEnd: target.end,
    columns,
    columnsStart,
    columnsEnd,
    bodyKind: body.bodyKind,
    valuesRows: body.valuesRows,
    selectSourceStart: body.selectSourceStart,
    selectSourceEnd: body.selectSourceEnd,
    returningStart: tail.returningStart,
    returningEnd: tail.returningEnd,
    onConflictStart: tail.onConflictStart,
    onConflictEnd: tail.onConflictEnd,
    onDuplicateStart: tail.onDuplicateStart,
    onDuplicateEnd: tail.onDuplicateEnd,
  };
}

interface InsertBody {
  readonly bodyKind: InsertBodyKind;
  readonly valuesRows: readonly ValuesRow[];
  readonly selectSourceStart: number | null;
  readonly selectSourceEnd: number | null;
  readonly bodyEndCursor: number;
}

function parseInsertBody(source: string, from: number, end: number, ctx: SkipContext): InsertBody {
  const word = readWord(source, from, end);
  if (!word) return { bodyKind: 'unknown', valuesRows: [], selectSourceStart: null, selectSourceEnd: null, bodyEndCursor: from };

  if (word.upper === 'VALUES') {
    const rows: ValuesRow[] = [];
    let cursor = word.end;
    while (cursor < end) {
      cursor = skipWsAndComments(source, cursor, end);
      if (cursor >= end || source[cursor] !== '(') break;
      const parenStart = cursor;
      const parenEnd = consumeParenGroup(source, parenStart, end, ctx);
      const bodyStart = parenStart + 1;
      const bodyInnerEnd = parenEnd - 1;
      const cells = splitTopLevelCommasToText(source, bodyStart, bodyInnerEnd, ctx);
      rows.push({ parenStart, parenEnd, bodyStart, bodyEnd: bodyInnerEnd, cells });
      cursor = skipWsAndComments(source, parenEnd, end);
      if (cursor < end && source[cursor] === ',') { cursor++; continue; }
      break;
    }
    return { bodyKind: 'values', valuesRows: rows, selectSourceStart: null, selectSourceEnd: null, bodyEndCursor: cursor };
  }

  if (word.upper === 'DEFAULT') {
    const after = skipWsAndComments(source, word.end, end);
    const next = readWord(source, after, end);
    if (next && next.upper === 'VALUES') {
      return { bodyKind: 'default-values', valuesRows: [], selectSourceStart: null, selectSourceEnd: null, bodyEndCursor: next.end };
    }
  }

  if (word.upper === 'SELECT' || word.upper === 'WITH' || word.upper === 'TABLE') {
    const tail = findTailKeyword(source, word.end, end, ctx);
    return {
      bodyKind: 'select',
      valuesRows: [],
      selectSourceStart: word.start,
      selectSourceEnd: tail.boundary,
      bodyEndCursor: tail.boundary,
    };
  }

  return { bodyKind: 'unknown', valuesRows: [], selectSourceStart: null, selectSourceEnd: null, bodyEndCursor: from };
}

interface InsertTail {
  readonly returningStart: number | null;
  readonly returningEnd: number | null;
  readonly onConflictStart: number | null;
  readonly onConflictEnd: number | null;
  readonly onDuplicateStart: number | null;
  readonly onDuplicateEnd: number | null;
}

function parseInsertTail(source: string, from: number, end: number, ctx: SkipContext): InsertTail {
  let onConflictStart: number | null = null;
  let onConflictEnd: number | null = null;
  let onDuplicateStart: number | null = null;
  let onDuplicateEnd: number | null = null;
  let returningStart: number | null = null;
  let returningEnd: number | null = null;

  let cursor = skipWsAndComments(source, from, end);
  while (cursor < end) {
    const w = readWord(source, cursor, end);
    if (!w) break;
    if (w.upper === 'ON') {
      const after = skipWsAndComments(source, w.end, end);
      const next = readWord(source, after, end);
      if (next && next.upper === 'CONFLICT') {
        onConflictStart = w.start;
        const tail = findTailKeyword(source, next.end, end, ctx);
        onConflictEnd = tail.boundary;
        cursor = tail.boundary;
        continue;
      }
      if (next && next.upper === 'DUPLICATE') {
        onDuplicateStart = w.start;
        const tail = findTailKeyword(source, next.end, end, ctx);
        onDuplicateEnd = tail.boundary;
        cursor = tail.boundary;
        continue;
      }
      break;
    }
    if (w.upper === 'RETURNING') {
      returningStart = w.end;
      const tail = findTailKeyword(source, w.end, end, ctx);
      returningEnd = tail.boundary;
      cursor = tail.boundary;
      continue;
    }
    break;
  }

  return { returningStart, returningEnd, onConflictStart, onConflictEnd, onDuplicateStart, onDuplicateEnd };
}

function looksLikeColumnList(source: string, parenStart: number, end: number): boolean {
  const after = skipWsAndComments(source, parenStart + 1, end);
  if (after >= end || source[after] === ')') return true;
  const w = readWord(source, after, end);
  if (!w) return true;
  const blocked = new Set(['SELECT', 'WITH', 'VALUES', 'TABLE']);
  return !blocked.has(w.upper);
}

interface TailKeywordMatch {
  readonly boundary: number;
}

const TAIL_KEYWORDS_NON_ON: ReadonlySet<string> = new Set(['RETURNING']);

/**
 * Walks paren-tracked from `from` and stops when it encounters a depth-0
 * `ON CONFLICT` / `ON DUPLICATE` / `RETURNING`. Returns the boundary position
 * (the start of the matched keyword), or `end` if no tail keyword is found.
 */
function findTailKeyword(
  source: string,
  from: number,
  end: number,
  ctx: SkipContext,
): TailKeywordMatch {
  let i = from;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren === 0 && IDENT_START.test(c) && isWordBoundaryBefore(source, i)) {
      const wStart = i;
      let j = i + 1;
      while (j < end && IDENT_CONT.test(source[j])) j++;
      const word = source.slice(wStart, j).toUpperCase();
      if (word === 'ON') {
        const after = skipWsAndComments(source, j, end);
        const next = readWord(source, after, end);
        if (next && (next.upper === 'CONFLICT' || next.upper === 'DUPLICATE')) {
          return { boundary: wStart };
        }
      } else if (TAIL_KEYWORDS_NON_ON.has(word)) {
        return { boundary: wStart };
      }
      i = j;
      continue;
    }
    i++;
  }
  return { boundary: end };
}
